import React, { useEffect, useRef, useState } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useQuizContext } from "./QuizContext"
import AnswerTile from "./AnswerTile"
import Button from "@/components/core/Button/Button"
import closeIcon from "@/public/icons/close-icon-black.svg"
import backArrow from "@/public/icons/arrow-right-black.svg"
import ideaIcon from "@/public/icons/idea-icon.svg"
import {
  fetchProducts,
  getFilterQuery,
  isAnswered,
  isFilterValid,
  navQuestion,
  sections,
  selectAnswer,
} from "./helper"

const Questionnaire = () => {
  const prevBtn = useRef(null)
  const queryClient = useQueryClient()
  const {
    setSection = () => {},
    totalQuestions,
    curQuestion,
    setCurQuestion,
    data = {},
    setData = () => {},
    quizAnalytics,
    eventIds,
  } = useQuizContext()
  const [showTooltip, setShowTooltip] = useState(false)
  const { questions = [] } = data
  const question = questions[curQuestion - 1]
  const answerClass =
    question?.answers?.length < 4 ? "col-6 col-md-4" : "col-6 col-md-3"
  const isMobile = navigator?.maxTouchPoints > 0

  const { isFetching, refetch } = useQuery({
    queryKey: ["stepData", curQuestion],
    queryFn: () => fetchProducts(getFilterQuery(questions), data?.plpCategory),
    staleTime: Infinity,
  })

  useEffect(() => {
    const nextQuestion = curQuestion + 1

    queryClient.prefetchQuery({
      queryKey: ["stepData", nextQuestion],
      queryFn: () =>
        fetchProducts(getFilterQuery(questions), data?.plpCategory),
    })

    document.addEventListener("click", () => {
      if (isMobile) {
        setShowTooltip(false)
      }
    })
  }, [questions, curQuestion, queryClient])

  const handleNext = () => {
    refetch()
      .then(({ data: d }) => {
        const { facet_counts: { facet_fields: facets = {} } = {} } = d
        isFilterValid(curQuestion, facets, setData)
        navQuestion(1, curQuestion, setCurQuestion, totalQuestions)
      })
      .catch(err => console.log(err))
      .finally(() => {
        prevBtn?.current?.scrollIntoView()
      })
  }

  const handleContinue = () => {
    setSection(sections.RESULTS)
  }

  const isLastQuestion = curQuestion === totalQuestions
  const handleToolTip = showTooltip => {
    if (!isMobile) {
      setShowTooltip(showTooltip)
    }
  }

  return (
    <div className="questionnaire kf-react-plp-container">
      <div className="row">
        <div className="col-12 d-flex justify-content-between">
          <button
            className={`top-controls previous${
              curQuestion < 2 ? " hidden" : ""
            }`}
            onClick={() => {
              quizAnalytics(eventIds.PREVIOUS)
              selectAnswer(curQuestion - 1, null, setData, true)
              navQuestion(-1, curQuestion, setCurQuestion, totalQuestions)
            }}
            ref={prevBtn}
          >
            <img src={backArrow.src} alt="close" /> Previous
          </button>
          <button
            className="top-controls close"
            onClick={() => {
              quizAnalytics(eventIds.CLOSE)
              setSection(sections.LANDING)
            }}
          >
            Close <img src={closeIcon.src} alt="close" />
          </button>
        </div>
      </div>
      <div className="row">
        <div className="col-9 col-md-6">
          <div className="progress-text">
            Question {curQuestion} of {totalQuestions}
          </div>
          <progress max={totalQuestions} value={curQuestion}></progress>
        </div>
      </div>
      <div className="row">
        <div className="col-12 question-text">
          <h2>{question?.questionsTitle}</h2>
          <div>
            <p>{question?.questionDescription}</p>
            {question?.info ? (
              <section>
                <button
                  onMouseOver={() => {
                    handleToolTip(true)
                  }}
                  onFocus={() => {
                    handleToolTip(true)
                  }}
                  onMouseOut={() => {
                    handleToolTip(false)
                  }}
                  onBlur={() => {
                    handleToolTip(false)
                  }}
                  onClick={e => {
                    if (isMobile) {
                      e.stopPropagation()
                      setShowTooltip(!showTooltip)
                    }
                  }}
                >
                  <img src={ideaIcon.src} alt="" />
                  Why We Ask?
                </button>
                {showTooltip ? (
                  <div
                    className="question-modal"
                    dangerouslySetInnerHTML={{ __html: question?.info }}
                  />
                ) : null}
              </section>
            ) : null}
          </div>
        </div>
      </div>
      <div className="row answer-row">
        {question?.answers.map((answer, answerIndex) =>
          !answer?.notApplicable ? (
            <div className={answerClass} key={curQuestion + answerIndex}>
              <AnswerTile
                key={curQuestion + answerIndex}
                data={{
                  title: answer?.answerTitle,
                  desc: answer?.answerDescription,
                  img: answer?.image,
                  isSelected: answer?.isSelected,
                }}
                onClick={() => {
                  quizAnalytics(
                    eventIds.ANSWER_SELECTED,
                    curQuestion,
                    answerIndex + 1,
                    question?.questionsTitle,
                    answer?.answerTitle
                  )
                  selectAnswer(curQuestion - 1, answerIndex, setData)
                }}
              />
            </div>
          ) : null
        )}
      </div>
      <div className="row controls">
        <div className="col-12 d-flex justify-content-end">
          <button
            className="skip-button"
            onClick={() => {
              quizAnalytics(eventIds.SKIP)
              if (isLastQuestion) {
                handleContinue()
              } else {
                selectAnswer(curQuestion - 1, null, setData, true)
                isFilterValid(curQuestion, null, setData, true)
                navQuestion(1, curQuestion, setCurQuestion, totalQuestions)
              }
            }}
          >
            Skip
          </button>
          <Button
            type="ghost"
            role="button"
            label={isLastQuestion ? "Continue" : "Next Question"}
            onClick={() => {
              if (isLastQuestion) {
                quizAnalytics(eventIds.CONTINUE)
                handleContinue()
              } else {
                quizAnalytics(eventIds.NEXT)
                handleNext()
              }
            }}
            size="small"
            disabled={!isAnswered(question?.answers)}
            loading={isFetching}
          />
        </div>
      </div>
    </div>
  )
}

export default Questionnaire
